import { API_CONFIG } from '@Web/config/api.config';
import { RestClient as RestClientImpl } from '@Web/clients/RestClient';
import { AuthService } from '@Web/services/AuthService';

//
// API RESPONSE INTERFACES (legacy round history lookup)
//
export interface RoundHistoryResponse {
  crashX: number;
  endedAt: number;
  clientSeed: string | null;
  roundNo: number;
  serverSeed: string | null;
  tableNo: number;
  serverSeedHash?: string; // backend optional
}

//
// CRASH PROVABLY FAIR CONTRACT TYPES
// (see crash-pf frontend technical contract; algorithmVersion kept as a string
//  because production uses crash-pf-hmac-drbg-hkdf-sha256-v1)
//
export type CrashPfSeedSource = 'BETTOR' | 'WATCHER' | 'FALLBACK';

export interface CrashPfRoundResponse {
  roundId: number;
  gameCode: number | string;
  nonce: number;
  algorithmVersion: string;
  serverSeedHash: string;
  serverSeedRevealed: string | null;
  clientSeed1?: string;
  clientSeed1Source?: CrashPfSeedSource;
  clientSeed1Ref?: string | null;
  clientSeed1OrderingReason?: string;
  clientSeed2?: string;
  clientSeed2Source?: CrashPfSeedSource;
  clientSeed2Ref?: string | null;
  clientSeed2OrderingReason?: string;
  clientSeed3?: string;
  clientSeed3Source?: CrashPfSeedSource;
  clientSeed3Ref?: string | null;
  clientSeed3OrderingReason?: string;
  hmacMessage?: string;
  hmacHex?: string;
  multiplier?: number;
  betPlayerCount?: number;
  watcherCount?: number;
  fallbackSeedCount?: number;
  frozenAt?: string | null;
  revealedAt?: string | null;
}

export interface CrashPfVerifyRequest {
  serverSeed: string;
  serverSeedHash: string;
  gameCode: number | string;
  roundId: number;
  nonce: number;
  clientSeed1: string;
  clientSeed2: string;
  clientSeed3: string;
  expectedHmacHex: string;
  expectedMultiplier?: number | null;
  algorithmVersion: string;
}

export interface CrashPfVerifyResponse {
  serverSeedHashValid: boolean;
  hmacValid: boolean;
  multiplierValid: boolean;
  valid: boolean;
  generatedHmacMessage: string;
  generatedHmacHex: string;
  generatedMultiplier?: number;
  algorithmVersion: string;
}

export interface CrashPfSettingsResponse {
  clientSeed?: string;
  serverSeedHash: string;
  roundId?: number;
}

/** Fair block delivered in the onReveal websocket event. */
export interface CrashPfRevealEventFairBlock {
  algo: string;
  serverSeedHash: string;
  serverSeed: string;
  multiplier: number;
  hmacMessage: string;
  hmacHex: string;
  seeds: Array<{
    seed: string;
    source: CrashPfSeedSource;
    sourceRef: string | null;
    orderingReason: string;
  }>;
  bettorCount: number;
  watcherCount: number;
  fallbackCount: number;
}

export interface CrashPfClientSeedRow {
  seed: string;
  source?: CrashPfSeedSource;
  ref?: string | null;
  orderingReason?: string;
}

type RawPfRecord = Record<string, unknown>;

function readNonEmptyString(
  obj: RawPfRecord,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

/** Normalize PF settings / commitment payloads from REST. */
export function normalizeCrashPfSettingsResponse(
  raw: unknown
): CrashPfSettingsResponse {
  const envelope = raw as { data?: unknown };
  const obj = (
    envelope?.data && typeof envelope.data === 'object' ? envelope.data : raw
  ) as RawPfRecord;

  const serverSeedHash =
    readNonEmptyString(
      obj,
      'serverSeedHash',
      'server_seed_hash',
      'nextServerSeedHash',
      'next_server_seed_hash'
    ) ?? '';

  const clientSeed = readNonEmptyString(
    obj,
    'clientSeed',
    'client_seed',
    'yourClientSeed'
  );

  const roundIdRaw = obj.roundId ?? obj.roundNo ?? obj.currentRoundId;
  const roundId =
    typeof roundIdRaw === 'number'
      ? roundIdRaw
      : typeof roundIdRaw === 'string' && roundIdRaw.length > 0
        ? Number(roundIdRaw)
        : undefined;

  return {
    clientSeed,
    serverSeedHash,
    roundId: Number.isFinite(roundId) ? roundId : undefined,
  };
}

function readSeedSource(
  obj: RawPfRecord,
  index: 1 | 2 | 3
): CrashPfSeedSource | undefined {
  const source = readNonEmptyString(
    obj,
    `clientSeed${index}Source`,
    `seed${index}Source`,
    `seed${index}ValueSource`,
    `client_seed${index}_source`
  );
  return source as CrashPfSeedSource | undefined;
}

function readSeedRef(
  obj: RawPfRecord,
  index: 1 | 2 | 3
): string | null | undefined {
  const ref = readNonEmptyString(
    obj,
    `seed${index}SourceRef`,
    `clientSeed${index}SourceRef`,
    `clientSeed${index}Ref`,
    `seed${index}Ref`,
    `seed${index}ValueRef`,
    `client_seed${index}_source_ref`,
    `client_seed${index}_ref`
  );
  if (ref !== undefined) {
    return ref;
  }

  const raw =
    obj[`seed${index}SourceRef`] ??
    obj[`clientSeed${index}SourceRef`] ??
    obj[`clientSeed${index}Ref`] ??
    obj[`seed${index}Ref`] ??
    obj[`seed${index}ValueRef`] ??
    obj[`client_seed${index}_source_ref`] ??
    obj[`client_seed${index}_ref`];
  if (raw == null) return raw as null | undefined;
  return typeof raw === 'string' ? raw : undefined;
}

function readSeedOrderingReason(
  obj: RawPfRecord,
  index: 1 | 2 | 3
): string | undefined {
  return readNonEmptyString(
    obj,
    `clientSeed${index}OrderingReason`,
    `seed${index}OrderingReason`,
    `client_seed${index}_ordering_reason`
  );
}

function readFlatClientSeed(
  obj: RawPfRecord,
  index: 1 | 2 | 3
): string | undefined {
  return readNonEmptyString(
    obj,
    `clientSeed${index}`,
    `seed${index}`,
    `seed${index}Value`,
    `client_seed${index}`
  );
}

function readSeedArrayEntry(entry: unknown): CrashPfClientSeedRow | null {
  if (!entry || typeof entry !== 'object') return null;
  const row = entry as RawPfRecord;
  const seed = readNonEmptyString(row, 'seed', 'clientSeed', 'value');
  if (!seed) return null;
  const source = readNonEmptyString(row, 'source', 'clientSeedSource') as
    | CrashPfSeedSource
    | undefined;
  const refValue = row.sourceRef ?? row.ref ?? row.clientSeedRef;
  return {
    seed,
    source,
    ref:
      typeof refValue === 'string' || refValue == null ? refValue : undefined,
    orderingReason: readNonEmptyString(
      row,
      'orderingReason',
      'ordering_reason'
    ),
  };
}

function assignClientSeedRow(
  round: CrashPfRoundResponse,
  index: 1 | 2 | 3,
  row: CrashPfClientSeedRow
) {
  if (index === 1) {
    round.clientSeed1 = row.seed;
    round.clientSeed1Source = row.source;
    round.clientSeed1Ref = row.ref ?? null;
    round.clientSeed1OrderingReason = row.orderingReason;
    return;
  }
  if (index === 2) {
    round.clientSeed2 = row.seed;
    round.clientSeed2Source = row.source;
    round.clientSeed2Ref = row.ref ?? null;
    round.clientSeed2OrderingReason = row.orderingReason;
    return;
  }
  round.clientSeed3 = row.seed;
  round.clientSeed3Source = row.source;
  round.clientSeed3Ref = row.ref ?? null;
  round.clientSeed3OrderingReason = row.orderingReason;
}

/** Normalize backend PF round payloads (seed1Value, seed1, clientSeed1, seeds[]). */
export function normalizeCrashPfRoundResponse(
  raw: unknown
): CrashPfRoundResponse {
  const envelope = raw as { data?: unknown };
  const obj = (
    envelope?.data && typeof envelope.data === 'object' ? envelope.data : raw
  ) as RawPfRecord;

  const serverSeedRevealed =
    readNonEmptyString(obj, 'serverSeedRevealed', 'serverSeed') ?? null;

  const seedRows: CrashPfClientSeedRow[] = [];
  for (const index of [1, 2, 3] as const) {
    const seed = readFlatClientSeed(obj, index);
    if (!seed) continue;
    seedRows.push({
      seed,
      source: readSeedSource(obj, index),
      ref: readSeedRef(obj, index),
      orderingReason: readSeedOrderingReason(obj, index),
    });
  }

  if (seedRows.length === 0) {
    const seeds = obj.seeds ?? obj.clientSeeds;
    if (Array.isArray(seeds)) {
      for (const entry of seeds) {
        const row = readSeedArrayEntry(entry);
        if (row) seedRows.push(row);
      }
    }
  }

  const round: CrashPfRoundResponse = {
    roundId: Number(obj.roundId ?? obj.roundNo ?? 0),
    gameCode: (obj.gameCode ?? obj.game_code ?? '') as number | string,
    nonce: Number(obj.nonce ?? obj.roundId ?? obj.roundNo ?? 0),
    algorithmVersion:
      readNonEmptyString(
        obj,
        'algorithmVersion',
        'algorithm_version',
        'algo'
      ) ?? '',
    serverSeedHash:
      readNonEmptyString(obj, 'serverSeedHash', 'server_seed_hash') ?? '',
    serverSeedRevealed,
    hmacMessage: readNonEmptyString(obj, 'hmacMessage', 'hmac_message'),
    hmacHex: readNonEmptyString(obj, 'hmacHex', 'hmac_hex'),
    multiplier:
      typeof obj.multiplier === 'number'
        ? obj.multiplier
        : typeof obj.crashX === 'number'
          ? obj.crashX
          : undefined,
    betPlayerCount:
      typeof obj.betPlayerCount === 'number'
        ? obj.betPlayerCount
        : typeof obj.bettorCount === 'number'
          ? obj.bettorCount
          : undefined,
    watcherCount:
      typeof obj.watcherCount === 'number' ? obj.watcherCount : undefined,
    fallbackSeedCount:
      typeof obj.fallbackSeedCount === 'number'
        ? obj.fallbackSeedCount
        : typeof obj.fallbackCount === 'number'
          ? obj.fallbackCount
          : undefined,
    frozenAt:
      typeof obj.frozenAt === 'string'
        ? obj.frozenAt
        : typeof obj.frozen_at === 'string'
          ? obj.frozen_at
          : null,
    revealedAt:
      typeof obj.revealedAt === 'string'
        ? obj.revealedAt
        : typeof obj.revealed_at === 'string'
          ? obj.revealed_at
          : null,
  };

  seedRows.slice(0, 3).forEach((row, index) => {
    assignClientSeedRow(round, (index + 1) as 1 | 2 | 3, row);
  });

  return round;
}

/** Ordered client seed rows for UI rendering (max 3). */
export function getCrashPfClientSeedRows(
  round: CrashPfRoundResponse | null | undefined
): CrashPfClientSeedRow[] {
  if (!round) return [];
  const rows: CrashPfClientSeedRow[] = [];
  const candidates = [
    {
      seed: round.clientSeed1,
      source: round.clientSeed1Source,
      ref: round.clientSeed1Ref,
      orderingReason: round.clientSeed1OrderingReason,
    },
    {
      seed: round.clientSeed2,
      source: round.clientSeed2Source,
      ref: round.clientSeed2Ref,
      orderingReason: round.clientSeed2OrderingReason,
    },
    {
      seed: round.clientSeed3,
      source: round.clientSeed3Source,
      ref: round.clientSeed3Ref,
      orderingReason: round.clientSeed3OrderingReason,
    },
  ];
  for (const row of candidates) {
    if (row.seed) rows.push({ ...row, seed: row.seed });
  }
  return rows;
}

/** Player label beside each client seed row — prefer sourceRef (username) over source type. */
export function getCrashPfSeedDisplayLabel(row: CrashPfClientSeedRow): string {
  const ref = row.ref?.trim();
  if (ref) return ref;
  return row.source ?? '—';
}

/**
 * When the seed slot is a system FALLBACK (source or ref), return the i18n key for the
 * translated display name. Real player usernames are left untouched.
 */
export function getCrashPfSeedFallbackNameKey(
  row: CrashPfClientSeedRow
): string | null {
  if (row.source === 'FALLBACK') {
    return 'ui.popuppanel.provably_fair.fallback_name';
  }
  const ref = row.ref?.trim();
  if (ref && ref.toUpperCase() === 'FALLBACK') {
    return 'ui.popuppanel.provably_fair.fallback_name';
  }
  return null;
}

/** i18n key for the role label (Top Bettor / Spectator / Fallback) from server seed source. */
export function getCrashPfSeedSourceLabelKey(
  source?: CrashPfSeedSource
): string {
  switch (source) {
    case 'WATCHER':
      return 'ui.popuppanel.provably_fair.watcher_n_label';
    case 'FALLBACK':
      return 'ui.popuppanel.provably_fair.fallback_n_label';
    case 'BETTOR':
    default:
      return 'ui.popuppanel.provably_fair.player_n_label';
  }
}

/** 1-based rank within the same seed source type (e.g. 2nd BETTOR → 2). */
export function getCrashPfSeedSourceRank(
  rows: CrashPfClientSeedRow[],
  index: number
): number {
  const source = rows[index]?.source ?? 'BETTOR';
  let rank = 0;
  for (let i = 0; i <= index; i++) {
    const rowSource = rows[i]?.source ?? 'BETTOR';
    if (rowSource === source) rank += 1;
  }
  return rank;
}

/** True once the server seed has been revealed and the round can be verified. */
export function isCrashPfRoundRevealed(
  round: CrashPfRoundResponse | null | undefined
): round is CrashPfRoundResponse & { serverSeedRevealed: string } {
  return !!round?.serverSeedRevealed;
}

/** HTTP status attached to fetch failures so the UI can render 404 / auth states. */
export class ProvablyFairRequestError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'ProvablyFairRequestError';
  }
}

//
// SERVICE
//
export class ProvablyFairService {
  private static instance: ProvablyFairService;
  private restClient: RestClientImpl;

  private constructor() {
    this.restClient = new RestClientImpl(API_CONFIG.baseUrl);
  }

  // Singleton instance
  public static getInstance(): ProvablyFairService {
    if (!ProvablyFairService.instance) {
      ProvablyFairService.instance = new ProvablyFairService();
    }
    return ProvablyFairService.instance;
  }

  // Auth headers
  private getHeaders() {
    const token = AuthService.getInstance().getAccessToken();

    return token
      ? {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      : { 'Content-Type': 'application/json' };
  }

  private toRequestError(error: unknown, fallback: string) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    return new ProvablyFairRequestError(fallback, status);
  }

  /**
   * Fetch the Provably Fair audit record for a single round.
   * GET /api/crash/provably-fair/round/{roundId}?gameCode&tableNo
   */
  public async getRound(
    gameCode: number | string,
    tableNo: number | string,
    roundId: number | string
  ): Promise<CrashPfRoundResponse> {
    try {
      const url = API_CONFIG.endpoints.provablyFairRound(
        roundId,
        gameCode,
        tableNo
      );
      const response = await this.restClient.get<unknown>(url, {
        headers: this.getHeaders(),
      });
      return normalizeCrashPfRoundResponse(response);
    } catch (error) {
      console.error('[ProvablyFair] getRound failed:', error);
      throw this.toRequestError(error, 'Invalid request');
    }
  }

  /**
   * Fetch the latest Provably Fair audit rows for a game (newest first).
   * GET /api/crash/provably-fair/history?gameCode&limit
   */
  public async getHistory(
    gameCode: number | string,
    limit = 10
  ): Promise<CrashPfRoundResponse[]> {
    try {
      const safeLimit = Math.max(1, Math.min(limit, 100));
      const url = API_CONFIG.endpoints.provablyFairHistory(gameCode, safeLimit);
      const response = await this.restClient.get<
        unknown[] | { data?: unknown[] }
      >(url, { headers: this.getHeaders() });
      const rows = Array.isArray(response) ? response : (response?.data ?? []);
      return rows.map(normalizeCrashPfRoundResponse);
    } catch (error) {
      console.error('[ProvablyFair] getHistory failed:', error);
      throw this.toRequestError(error, 'Failed to load Provably Fair history.');
    }
  }

  /**
   * Submit revealed round values to the server-side verifier.
   * POST /api/crash/provably-fair/verify
   */
  public async verify(
    request: CrashPfVerifyRequest
  ): Promise<CrashPfVerifyResponse> {
    try {
      const response = await this.restClient.post<CrashPfVerifyResponse>(
        API_CONFIG.endpoints.provablyFairVerify,
        request,
        { headers: this.getHeaders() }
      );
      return response;
    } catch (error) {
      console.error('[ProvablyFair] verify failed:', error);
      throw this.toRequestError(error, 'Verification request failed.');
    }
  }

  /**
   * Build a verify request from a revealed round audit and call the verifier.
   * Send clientSeed1/2/3 exactly as received (SEC-006 — do not reorder).
   */
  public async verifyRoundFromAudit(
    round: CrashPfRoundResponse
  ): Promise<CrashPfVerifyResponse> {
    if (!isCrashPfRoundRevealed(round)) {
      throw new ProvablyFairRequestError('Round not revealed yet');
    }
    const request: CrashPfVerifyRequest = {
      serverSeed: round.serverSeedRevealed,
      serverSeedHash: round.serverSeedHash,
      gameCode: round.gameCode,
      roundId: round.roundId,
      nonce: round.nonce,
      clientSeed1: round.clientSeed1 ?? '',
      clientSeed2: round.clientSeed2 ?? '',
      clientSeed3: round.clientSeed3 ?? '',
      expectedHmacHex: round.hmacHex ?? '',
      expectedMultiplier: round.multiplier ?? null,
      algorithmVersion: round.algorithmVersion,
    };
    return this.verify(request);
  }

  /**
   * Fetch live PF settings (client seed + next/current server seed commitment).
   * GET /api/crash/round/provablyfair?gameCode&tableNo
   */
  public async getSettings(
    gameCode: number | string,
    tableNo: number | string
  ): Promise<CrashPfSettingsResponse> {
    try {
      const url = `${API_CONFIG.endpoints.provablyFair}?gameCode=${gameCode}&tableNo=${tableNo}`;
      const response = await this.restClient.get<unknown>(url, {
        headers: this.getHeaders(),
      });
      return normalizeCrashPfSettingsResponse(response);
    } catch (error) {
      console.error('[ProvablyFair] getSettings failed:', error);
      throw this.toRequestError(
        error,
        'Failed to load Provably Fair settings.'
      );
    }
  }

  /**
   * Resolve the active server seed commitment (SHA-256) for the settings UI.
   * Tries the settings endpoint first, then round audit lookups.
   */
  public async resolveActiveServerSeedHash(
    gameCode: number | string,
    tableNo: number | string,
    roundIdCandidates: number[] = []
  ): Promise<string | null> {
    try {
      const settings = await this.getSettings(gameCode, tableNo);
      if (settings.serverSeedHash) return settings.serverSeedHash;
    } catch {
      // Fall through to per-round audit lookups.
    }

    const uniqueIds = [...new Set(roundIdCandidates.filter(id => id > 0))];
    for (const roundId of uniqueIds) {
      try {
        const round = await this.getRound(gameCode, tableNo, roundId);
        if (round.serverSeedHash) return round.serverSeedHash;
      } catch {
        // Try the next candidate round id.
      }
    }

    return null;
  }

  /**
   * Legacy round history lookup (kept for backward compatibility).
   * GET /api/crash/round/:tableNo/:roundNo
   */
  public async getRoundHistory(
    tableNo: number,
    roundNo: number
  ): Promise<RoundHistoryResponse | null> {
    try {
      const url = `/api/crash/round/${tableNo}/${roundNo}`;

      const response = await this.restClient.get<RoundHistoryResponse>(url, {
        headers: this.getHeaders(),
      });

      return response || null;
    } catch (error) {
      console.error('Failed to fetch round history:', error);
      return null;
    }
  }
}
